// run-command-mattermost-transport-wiring.ts — P19c: builds the REAL
// `MattermostTransportDeps` for `avatar autopilot run`.
//
// "0 caller" #10 (see authority-critical-functions test): before this file,
// `run-command-cli-wiring.ts` never referenced `mattermostTransport`, so
// supervisor-transport-tick.ts could only ever take `local_only` — every
// chat directive, approve G3 included, was unreachable.
//
// Fail-soft by design: anything missing (no credentials, no root post, no
// approver) returns `{enabled:false, reason}` (phase 05 Req 1 — was a bare
// `undefined`), which the caller still treats as local-only. Never hands
// back a transport that would LOOK wired while rejecting every post as
// `wrong_thread`.
//
// X6: the credentials/channel/base_url/approver checks are NOT written here —
// they live in mattermost-transport-preconditions.ts, shared with the run
// factory and `autopilot doctor`. This file keeps only `no_root_post` (its own
// input) and, below, the transport it builds once those pass.
import { readOutboxOperation } from "./delivery-outbox.js";
import {
  CHAT_REPLY_OUTBOX_KIND,
  deliverChatReplyOperation,
} from "./mattermost-chat-command-reply.js";
import { readMattermostCredentials } from "./mattermost-credentials-file.js";
import { MattermostRestClient } from "./mattermost-rest-client.js";
import { deliverRootPostIntent, type RootPostSender } from "./mattermost-root-post-manager.js";
import {
  evaluateMattermostConfigPreconditions,
  MATTERMOST_DISABLED_REASONS,
  type MattermostDisabledReason,
} from "./mattermost-transport-preconditions.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";
import type { MattermostTransportDeps } from "./supervisor-transport-tick.js";

// X6: the reasons moved to mattermost-transport-preconditions.ts; re-exported so
// every existing importer of this module keeps compiling unchanged.
export { MATTERMOST_DISABLED_REASONS, type MattermostDisabledReason };

/** Req 1: `enabled:true` carries what the D8 banner needs (channelId); `enabled:false` carries WHY. */
export type MattermostTransportDepsOutcome =
  | { enabled: true; deps: MattermostTransportDeps; channelId: string }
  | { enabled: false; reason: MattermostDisabledReason };

export interface BuildMattermostTransportDepsInput {
  workspaceRoot: string;
  controlRoot: string;
  missionId: string;
  config: AutopilotConfig;
  /** Epoch ms of mission creation — posts at or before it are ignored (§7.2). */
  missionCreatedAtMs: number;
  /** The mission's recorded root post; absent => no transport (see header). */
  rootPostId?: string;
  /** Bot's own user id, so its own posts are never parsed as directives. */
  botUserId?: string;
  /** Test seam: replaces the real REST-backed sender. Never set in production. */
  senderForTest?: RootPostSender;
  /** Reuse caller's already-built client instead of a second one against the same origin/token. Absent => builds its own (unit tests). */
  client?: MattermostRestClient;
}

/**
 * Builds the transport deps outcome — `{enabled:false, reason}` for local-only.
 *
 * `outboxSend` is deliberately routed through `deliverRootPostIntent` and
 * never calls `createPost` directly: outbox-delivery-worker.ts's H1
 * contract requires a reconciliation-aware send, because an ambiguous
 * failure (5xx/network) may already have committed the post server-side and
 * a blind retry would create a duplicate.
 */
export async function buildMattermostTransportDeps(
  input: BuildMattermostTransportDepsInput,
): Promise<MattermostTransportDepsOutcome> {
  // No recorded root post => every reply would be rejected `wrong_thread`.
  if (input.rootPostId === undefined || input.rootPostId.length === 0) {
    return { enabled: false, reason: MATTERMOST_DISABLED_REASONS.no_root_post };
  }

  // X6: the shared verdict (credentials → channel → base_url → approver — the
  // run path's order). `boundUserId` is the human whose chat identity is
  // authorised for this mission (operator-owned config — never worker output).
  const verdict = evaluateMattermostConfigPreconditions({
    config: input.config,
    credentials: await readMattermostCredentials(input.workspaceRoot),
  });
  if (!verdict.enabled) return verdict;
  const { botToken, channelId, baseUrl, approverUserId: boundUserId } = verdict;

  const client =
    input.client ??
    new MattermostRestClient({
      baseUrl,
      botToken,
      timeoutMs: input.config.network_timeout_sec * 1000,
    });

  const sender: RootPostSender = input.senderForTest ?? {
    createPost: (channel, message, rootId) => client.createPost(channel, message, rootId),
  };

  const botUserId = input.botUserId ?? "";

  const deps: MattermostTransportDeps = {
    outboxMaxAttemptsPerTick: input.config.outbox_max_attempts_per_tick,
    outboxRetryBackoffMs: input.config.outbox_retry_backoff_ms,
    outboxSend: async (eventId: string) => {
      const op = await readOutboxOperation(input.controlRoot, eventId);
      if (op?.payload === undefined) {
        // Pre-P19a op: no recorded message. Inventing one would post
        // arbitrary text into a real channel, so refuse — the worker leaves
        // the op `uncertain` and an operator can inspect it.
        throw new Error(
          `outboxSend: op "${eventId}" không có payload — không thể dựng lại nội dung post.`,
        );
      }
      // Chat replies are best effort with an attempt cap (review 261006 M4).
      if (op.kind === CHAT_REPLY_OUTBOX_KIND) {
        return deliverChatReplyOperation({
          controlRoot: input.controlRoot,
          eventId,
          createPost: sender.createPost,
        });
      }
      const delivered = await deliverRootPostIntent({
        controlRoot: input.controlRoot,
        channelId: op.payload.channel_id,
        eventId,
        missionId: input.missionId,
        sequence: op.sequence,
        message: op.payload.message,
        sender,
        ...(op.payload.root_id ? { rootId: op.payload.root_id } : {}),
        ...(op.payload.correlation_id ? { correlationId: op.payload.correlation_id } : {}),
      });
      if (delivered.status !== "sent" || delivered.remoteId === undefined) {
        // Throwing is what tells the worker to leave the op `uncertain`;
        // deliverRootPostIntent has ALREADY tried correlation recovery, so
        // the worker must not (and does not) blind-retry after this.
        throw new Error(`outboxSend: giao "${eventId}" chưa xác nhận (${delivered.status}).`);
      }
      return { remoteId: delivered.remoteId };
    },
    directiveSync: {
      client,
      channelId,
      botUserId,
      boundUserId,
      expectedRootId: input.rootPostId,
      missionCreatedAtMs: input.missionCreatedAtMs,
      hintThrottleSec: input.config.abort_challenge_ttl_sec,
    },
  };

  return { enabled: true, deps, channelId };
}
